import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPermissions } from '../../lib/accessControl';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { abortController } from '../../utils/abortController';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import usePageFilterPersistence from '../../hooks/usePageFilterPersistence';
import MaterialReactDataTable from '../../components/MaterialReactDataTable';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { Modal } from 'react-bootstrap';
import VirtualizedAutocomplete from '../../components/Autocomplete';
import useTab from '../../hooks/useTab';
import { Checkbox, FormControlLabel } from '@mui/material';

const Deals = ({ isLoading = false, selectedBusinessUnit = null, isFertilizersAndPetroChemicalsDeal = false, isFoodsOrPolymersDeal = false, setActiveTab = () => { }, setSelectedDeal = () => { }, setSelectedPFI = () => { }, setIsLoading = () => { }, setShowAlert = () => { }, setAlertMessage = () => { }, setAlertType = () => { }, logOut = () => { } }) => {

  const {
    setSelectedTranche,
    setSelectedLinkedDeal = () => { },
    setSelectedLinkedPFI = () => { },
    setIsNewPFI = () => { },
  } = useTab();

  const { pathname } = useLocation();
  const location = useLocation();

  /* AUTHENTICATION VARIABLES */
  const currentUser = useUser();

  /* AUTHORIZATION VARIABLES */
  const { isWritable = false } = getPermissions(currentUser?.role, location?.pathname);

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* PERSISTED FILTERS (redux, keyed by route) */
  // Shared explicitly with MaterialReactDataTable (via the pageKey prop below)
  // so both this page and the table read/write the same persisted slot for
  // the global search value.
const pageKey = `deals-${selectedBusinessUnit || "default"}`; 
  const {
    filterValues: persistedFilters,
    setFilter: persistFilter
  } = usePageFilterPersistence(pageKey);
  const hasHydratedGlobalFilterRef = useRef(false);
  const persistDebounceRef = useRef(null);

  /* DEAL VARIABLES */
  const [dealData, setDealData] = useState([]);
  const [newDeal, setNewDeal] = useState({});
  const tableRef = useRef();

  /* DATATABLE ROW VARIABLES */
  const [rowData, setRowData] = useState({});
  const [dealUnlockReason, setDealUnlockReason] = useState(null);


  /* LIST VARIABLES */
  const [tranches, setTranches] = useState([]);
  const [isPeriodLockTranchesLoading, setIsPeriodLockTranchesLoading] = useState(false);
  const [tranchesToUnlock, setTranchesToUnlock] = useState([]);
  const [unlockRequested, setUnlockRequested] = useState(false);

  // FILTER VARIABLES 
  const [selectedTranches, setSelectedTranches] = useState([]);

  /* DATATABLE GLOBAL FILTER VARIABLES */
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  /* DIALOG VARIABLES */
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  /* MODAL VARIABLES */
  const [showUnlockDealModal, setShowUnlockDealModal] = useState(false);

  // ENTITY VARIABLES 
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const [entitiesForCopy, setEntitiesForCopy] = useState([]);
  const [isEntitiesLoading, setIsEntitiesLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // LINK DEAL
  const [createLinkDeal, setCreateLinkDeal] = useState(false);


  // LINK DEAL MODAL VARIABLES
  const [showLinkDealModal, setShowLinkDealModal] = useState(false);
  const [dealListForAutoPopulate, setDealListForAutoPopulate] = useState([]);
  const [isDealsListForCopyLoading, setisDealsListForCopyLoading] = useState(false);
  const [selectedDealForAutopopulate, setSelectedDealForAutopopulate] = useState(null);

  useEffect(() => {
    const fetchEntitiesList = async () => {
      setSelectedEntity(null);
      setIsEntitiesLoading(true);
      try {
        const response = await axios({
          method: "post",
          url: "/api/GetEntity",
          data: {
            business_unit_id: selectedBusinessUnit,
          },
          signal: abortController.signal
        });

        if (response.status === 200) {
          const apiEntities = response.data?.data || [];
          setEntitiesForCopy(apiEntities);
        } else {
          setAlertMessage("Failed to load entities.");
          setAlertType("error");
          setShowAlert(true);
        }
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error("GetDealListEntity API error:", error);
        setAlertMessage("Error loading entities for copy.");
        setAlertType("error");
        setShowAlert(true);
      } finally {
        setIsEntitiesLoading(false);
      }
    }
    fetchEntitiesList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    const fetchLists = async () => {
      setIsPeriodLockTranchesLoading(true);
      axios({
        method: "post",
        url: "/api/GetPeriodLockTrancheList",
        data: {
          pathname,
          Business_Unit_Id: selectedBusinessUnit,
          Deal_PFI_Id: rowData?.deal_pfi_id
        },
        signal: abortController.signal
      })
        .then((response) => {
          setIsPeriodLockTranchesLoading(false);
          const { status, data } = response;

          if (status === 200) {
            const tranches = data?.tranches_list || [];
            setTranches(tranches);
          }
        })
        .catch((error) => {
          setIsPeriodLockTranchesLoading(false);
          console.log("Get Tranche List Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });
    }

    if (rowData?.deal_pfi_id) {
      fetchLists();
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [rowData]);

  const prevBusinessUnitRef = useRef(null);
  const abortControllerRef = useRef(new AbortController());

const fetchDealData = async (businessUnitId, isNewDeal = false, filterValueOverride = null) => {
  if (!businessUnitId) return;

  abortControllerRef.current.abort();
  abortControllerRef.current = new AbortController();

  setIsLoading(true);

  if (!isNewDeal) {
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");
  }
  try {
    const response = await axios({
      method: "post",
      url: "/api/GetDealData",
      data: {
        business_unit_id: businessUnitId,
        // Use the explicit override when provided (business-unit switch);
        // otherwise fall back to current state (manual search / refresh calls).
        filter_value: filterValueOverride !== null ? filterValueOverride : globalFilterValue
      },
      signal: abortControllerRef.current.signal
    });

      setIsLoading(false);
      const { status, data } = response;

      if (status === 200) {
        const dealData = data?.deal_data || [];
        setDealData(dealData);
      } else {
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
      }
    } catch (error) {
      setIsLoading(false);

      // Check cancellation BEFORE logging: a request we deliberately aborted
      // (e.g. because selectedBusinessUnit changed again quickly) is expected
      // and not worth logging as an error.
      if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
        return;
      }

      console.error("Get Deal Data Api: ", error);

      const status = error?.response?.status;

      if (status === 403) {
        triggerSessionExpire();
      } else {
        setAlertMessage(
          status === 401
            ? "Unauthorized access. You do not have the required permissions to perform this action."
            : status === 429
              ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
              : "An error occurred while processing your request. Please try again later or contact the site administrator."
        );
        setAlertType("error");
        setShowAlert(true);

        if (status === 429) {
          setTimeout(logOut, 3000);
        }
      }
    }
  };

useEffect(() => {
  if (!selectedBusinessUnit || selectedBusinessUnit === prevBusinessUnitRef.current) {
    return;
  }
  prevBusinessUnitRef.current = selectedBusinessUnit;

  const resolvedFilterValue = persistedFilters?.globalFilterValue || "";
  setGlobalFilterValue(resolvedFilterValue);

  const timer = setTimeout(() => {
    fetchDealData(selectedBusinessUnit, false, resolvedFilterValue); // ← explicit, no closure risk
  }, 100);

  return () => {
    clearTimeout(timer);
    abortControllerRef.current.abort();
  };
}, [selectedBusinessUnit]);

  /* -------- PERSISTED GLOBAL SEARCH: hydrate once on mount -------- */
  // redux-persist rehydrates the store before this component renders, so
  // persistedFilters.globalFilterValue is already available on first mount -
  // this runs before the effect above fires for a subsequently-selected
  // business unit, so that fetch (if any) picks up the persisted value.
  useEffect(() => {
    if (hasHydratedGlobalFilterRef.current) return;
    hasHydratedGlobalFilterRef.current = true;

    const persistedValue = persistedFilters?.globalFilterValue;

    if (persistedValue) {
      setGlobalFilterValue(persistedValue);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

const prevBusinessUnitForFilterRef = useRef(selectedBusinessUnit);

useEffect(() => {
  if (prevBusinessUnitForFilterRef.current === selectedBusinessUnit) return;
  prevBusinessUnitForFilterRef.current = selectedBusinessUnit;

  const persistedValue = persistedFilters?.globalFilterValue;
  setGlobalFilterValue(persistedValue || "");
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
}, [selectedBusinessUnit]);
  useEffect(() => {
    if (!hasHydratedGlobalFilterRef.current) return;

    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
    }

    persistDebounceRef.current = setTimeout(() => {
      persistFilter('globalFilterValue', globalFilterValue || "");
    }, 400);

    return () => clearTimeout(persistDebounceRef.current);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [globalFilterValue]);

  const handleOpenAddDealModal = async (shouldClone = false, old_deal_id = null, entity_id = null) => {
    setSelectedEntity(null);
    setCreateLinkDeal(false);
    if (shouldClone) {
      handleAddDeal(true, old_deal_id, entity_id);
    } else {
      setShowAddDealModal(true);

    }

  };


  // Open Link Deal modal and fetch available deals
  const handleOpenLinkDealModal = async (row) => {
    setRowData(row); // to access entity_id later
    setSelectedDealForAutopopulate(null);
    setShowLinkDealModal(true);
    await fetchDealsList(row, entitiesForCopy);
  };

  // Fetch list of deals for auto-populate
  const fetchDealsList = async (row, entitiesForCopy) => {
    setisDealsListForCopyLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");
    const otherEntity = entitiesForCopy?.find(
      (entity) => entity.id !== row?.entity_id
    );
    try {
      const response = await axios({
        method: "post",
        url: "/api/GetDealListForAutoPopulate",
        data: {
          pathname,
          Business_Unit_Id: selectedBusinessUnit,
          entity_id: otherEntity?.id
        },
        signal: abortController.signal,
      });

      setisDealsListForCopyLoading(false);
      if (response.status === 200) {
        setDealListForAutoPopulate(response.data?.deals || []);
      } else {
        setAlertMessage("An error occurred while fetching deals.");
        setAlertType("error");
        setShowAlert(true);
      }
    } catch (error) {
      setisDealsListForCopyLoading(false);

      if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

      console.error("Get Deals List API: ", error);

      const status = error?.response?.status;
      if (status === 403) {
        triggerSessionExpire();
      } else {
        setAlertMessage(
          status === 401 ? "Unauthorized access."
            : status === 429 ? "Request limit exceeded."
              : "An error occurred while fetching deals."
        );
        setAlertType("error");
        setShowAlert(true);

        if (status === 429) setTimeout(logOut, 3000);
      }
    }
  };

  // Handle the actual linking
  const handleLinkDeal = async () => {
    if (!selectedDealForAutopopulate) {
      setAlertMessage("Please select a deal to auto-populate from.");
      setAlertType("error");
      setShowAlert(true);
      return;
    }

    setIsLoading(true);
    setShowAlert(false);
    try {
      const response = await axios({
        method: "POST",
        url: "/api/SetLinkedDealReference", // ← adjust if endpoint name is different
        data: {
          business_unit_id: selectedBusinessUnit,
          entity_id: rowData?.entity_id,           // from current row
          deal_no: rowData?.deal_id,
          linked_deal_id: selectedDealForAutopopulate?.id,
          user_id: currentUser?.id
        },
        signal: abortController.signal
      });

      setIsLoading(false);
      if (response.status === 200) {
        setShowLinkDealModal(false);
        setAlertMessage("Deal Linked successfully.");
        setAlertType("success");
        setShowAlert(true);
        fetchDealData(selectedBusinessUnit, true); // refresh table
      } else {
        setAlertMessage("Failed to auto-populate deal.");
        setAlertType("error");
        setShowAlert(true);
      }
    } catch (error) {
      setIsLoading(false);
      if (axios.isCancel(error)) return;

      console.error("Link Deal API: ", error);

      const status = error?.response?.status;
      if (status === 403) triggerSessionExpire();
      else {
        setAlertMessage(
          status === 401 ? "Unauthorized access."
            : status === 429 ? "Request limit exceeded."
              : "An error occurred while linking the deal."
        );
        setAlertType("error");
        setShowAlert(true);
        if (status === 429) setTimeout(logOut, 3000);
      }
    }
  };

  const handleAddDeal = async (shouldClone = false, old_deal_id = null, entity_id = null) => {
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");
    setSelectedTranche(null);
    setSelectedLinkedDeal(null);
    setSelectedLinkedPFI(null);

    const apiUrl = createLinkDeal
      ? "/api/GenerateLinkedDealNo"
      : "/api/GenerateDealNo";

    const payload = {
      business_unit_id: selectedBusinessUnit,
      user_id: currentUser?.id,
    };

    if (!createLinkDeal) {
      payload.entity_id = entity_id || selectedEntity?.id;
    }

    await axios({
      method: "POST",
      url: apiUrl,
      data: payload,
      signal: abortController.signal
    })
      .then((response) => {
        setIsLoading(false);
        const { status, data } = response;

        if (status === 200) {
          setShowAddDealModal(false);
          setNewDeal(data);
          if (shouldClone) {
            handleCloneDeal(data, old_deal_id);
          } else {
            setAlertMessage(
              createLinkDeal
                ? "Linked deal created successfully."
                : `New deal created successfully: ${data?.value}`
            );
            setAlertType("success");
            setShowAlert(true);
            fetchDealData(selectedBusinessUnit, true); // Fetch updated deal data after adding a new deal

          }
        }
        else {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => {
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return;
        }

        console.error("Generate Deal No. Api: ", error);

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        }
        else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      });
  }

  const handleAddDealPFIWrapper = (rowData) => {
    setRowData(rowData);
    setShowConfirmationDialog(true);
  }



  const handleAddDealPFI = async () => {
    setShowConfirmationDialog(false);
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");
    setSelectedTranche(null);
    setIsNewPFI(true);
    console.log(rowData, 'rowData')
    const { deal_id, "Deal No.": dealNo, is_route_linked_deal, linked_deal_id } = rowData;
    const mainDeal =
    {
      id: deal_id,
      value: dealNo,
      is_route_linked_deal: rowData?.is_route_linked_deal,
      is_vantira_deal: Boolean(rowData?.entity_id === 2)
    };


    const createPFI = async (dealNoId) => {
      try {
        const response = await axios({
          method: "POST",
          url: "/api/AddDealPFI",
          data: {
            business_unit_id: selectedBusinessUnit,
            deal_no: dealNoId,
            user_id: currentUser?.id
          },
          signal: abortController.signal
        });

        if (response.status === 200) {
          return response.data; // { id, value, ... }
        }
        throw new Error(`Unexpected status: ${response.status}`);
      } catch (error) {
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return null; // cancelled → silent return
        }
        throw error;
      }
    };

    try {
      // 1. Always create PFI for the main deal
      const mainPFIData = await createPFI(mainDeal.id);

      // 2. If it's a route-linked deal → create second PFI
      if (Boolean(is_route_linked_deal) && linked_deal_id) {
        await createPFI(linked_deal_id);
      }

      // ── Success path ───────────────────────────────────────
      setIsLoading(false);

      // You now have one or two PFIs created
      // Decide UI behavior here (examples below)

      // Option A: Select the main one (most common)
      setActiveTab("Pre-Shipment");
      setSelectedDeal(mainDeal);
      setSelectedPFI({
        id: mainPFIData?.id,
        value: mainDeal.value + mainPFIData?.value,
        is_vantira_deal: Boolean(rowData?.entity_id === 2)
      });
      setSelectedLinkedDeal(null);
      setSelectedLinkedPFI(null);
      setNewDeal({});

      // Option B: If linked deal exists, maybe set a "primary + secondary" state
      // if (linkedPFIData) {
      //   setSelectedLinkedPFI({
      //     id: linkedPFIData.id,
      //     value: /* fetch linked deal number or compute */
      //   });
      // }

    } catch (error) {
      setIsLoading(false);
      console.error("Generate PFI Api: ", error);

      const status = error?.response?.status;

      if (status === 403) {
        triggerSessionExpire();
        return;
      }

      setAlertMessage(
        status === 401
          ? "Unauthorized access. You do not have the required permissions."
          : status === 429
            ? "Request limit exceeded. Account temporarily disabled."
            : "An error occurred while processing your request."
      );
      setAlertType("error");
      setShowAlert(true);

      if (status === 429) {
        setTimeout(logOut, 3000);
      }
    }
  };

  const handleCloneDeal = async (clonedDeal = null, old_deal_id = null) => {

    if (!clonedDeal) return;

    const apiUrl = isFertilizersAndPetroChemicalsDeal
      ? "/api/CloneDealFertilizersAndPetroChemicals"
      : isFoodsOrPolymersDeal
        ? "/api/CloneDealFoodsAndPolymers"
        : "";

    setShowConfirmationDialog(false);
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    const selectedDeal = clonedDeal;

    await axios({
      method: "POST",
      url: apiUrl,
      data: {
        Business_Unit_Id: selectedBusinessUnit,
        new_deal_id: clonedDeal?.id,
        old_deal_id: old_deal_id,
        user_id: currentUser?.id
      },
      signal: abortController.signal
    })
      .then((response) => {
        setIsLoading(false);
        const { status } = response;

        if (status === 200) {
          setAlertMessage(`Deal cloned successfully: ${selectedDeal?.value}`);
          setAlertType("success");
          setShowAlert(true);
          setSelectedDeal(selectedDeal);
          fetchDealData(selectedBusinessUnit, true);
        }
        else {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => {
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return;
        }

        console.error("Generate PFI Api: ", error);

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        }
        else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      });
  }

  const handleEditDeal = (rowData, tab) => {
    console.log(rowData, 'rowData')
    const {
      deal_id: dealId,
      deal_pfi_id: dealPFIId,
      "Deal No.": dealNo,
      deal_pfi: pfiNo,

    } = rowData;
    setSelectedTranche(null)
    setSelectedDeal
      ({
        id: dealId,
        value: dealNo,
        is_route_linked_deal: rowData?.is_route_linked_deal,
        is_vantira_deal: Boolean(rowData?.entity_id === 2)
      });
    setSelectedPFI({
      id: dealPFIId,
      value: pfiNo,
      period_lock: rowData?.period_lock,
      is_vantira_deal: Boolean(rowData?.entity_id === 2)
    });
    if (rowData?.linked_deal_id) {

      setSelectedLinkedDeal(null);
      setSelectedLinkedPFI({ id: rowData?.linked_deal_pfi_id, value: rowData?.linked_deal_pfi })
    } else {
      setSelectedLinkedDeal(null)
      setSelectedLinkedPFI(null)
    }
    setActiveTab(tab);
  }

  const handleUnlockDealWrapper = (rowData) => {
    setRowData(rowData);
    setShowUnlockDealModal(true);
    setDealUnlockReason(null)
    setSelectedTranches([])
  }

  const handleUnlockDeal = useCallback(async (id) => {
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    await axios({
      method: "post",
      url: "/api/SubmitPeriodUnlockRequest",
      data: {
        business_unit_id: selectedBusinessUnit,
        user_id: currentUser?.id,
        deal_pfi_id: rowData?.deal_pfi_id,
        tranche_ids: selectedTranches?.map((tranche) => tranche?.id),
        reason: dealUnlockReason || null
      },
      signal: abortController.signal
    })
      .then((response) => {
        setIsLoading(false);
        const { status, data } = response;

        if (status === 200) {
          setShowUnlockDealModal(false);
          setAlertMessage(data?.data)
          setAlertType("success");
          setShowAlert(true);
          setUnlockRequested(true)
          setTranchesToUnlock((prev) => [
            ...prev,
            ...selectedTranches.map((tranche) => ({
              ...tranche,
              deal_pfi_id: rowData?.deal_pfi_id
            }))
          ]);
          setDealUnlockReason(null);
        }
        else {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => {
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return;
        }

        console.log("Set Bank Status Api: ", error);

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        }
        else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      });

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedBusinessUnit, currentUser?.id, rowData?.deal_pfi_id, selectedTranches, dealUnlockReason]
  );

  const handleGlobalFilter = async (rowData) => {
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    await axios({
      method: "post",
      url: "/api/FilterTableData",
      data: {
        pathname: location?.pathname,
        business_unit_id: selectedBusinessUnit,
        table_data: rowData,
        filter_value: globalFilterValue
      },
      signal: abortController.signal
    })
      .then((response) => {
        setIsLoading(false);
        const { status, data } = response;

        if (status === 200) {
          tableRef?.current?.setFilteredData(data || []);
        }
        else {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => {
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return;
        }

        console.log("Filter Table Data Api: ", error);

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        }
        else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      });
  }

  const handleCloseConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  }

  const handleCloseModal = () => {
    setShowUnlockDealModal(false);
  }


  return (
    <div className="d-flex w-100 flex-column gap-2 justify-content-center px-2">
      <Dialog open={showConfirmationDialog} onClose={handleCloseConfirmationDialog}>
        <DialogContent>
          <DialogContentText className="text-center">
            Are you sure you want to proceed with adding a new PFI?
          </DialogContentText>
        </DialogContent>
        <DialogActions>

          <button
            type="button"
            className="btn btn-danger custom-btn"
            onClick={handleCloseConfirmationDialog}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-secondary custom-btn"
            onClick={handleAddDealPFI}
          >
            Add
          </button>
        </DialogActions>
      </Dialog>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MaterialReactDataTable
          ref={tableRef}
          title="Deals"
          pageKey={pageKey}
          isWritable={isWritable}
          isLoading={isLoading}
          isBusinessUnitSelected={true}
          businessUnitId={selectedBusinessUnit}
          newDeal={newDeal}
          globalFilterValue={globalFilterValue}
          tableData={dealData}
          unlockRequested={unlockRequested}
          lockedTranchesList={tranches}
          tranchesToUnlock={tranchesToUnlock}
          setIsLoading={setIsLoading}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          setGlobalFilterValue={setGlobalFilterValue}
          handleGlobalFilter={handleGlobalFilter}
          handleAddDeal={handleOpenAddDealModal}
          handleLinkDeal={handleOpenLinkDealModal}
          handleAddPFI={handleAddDealPFIWrapper}
          handleUnlockDeal={handleUnlockDealWrapper}
          handleEditDeal={handleEditDeal}
        />
      </LocalizationProvider>

      <Modal show={showUnlockDealModal} onHide={handleCloseModal} centered={true}>
        <Modal.Header style={{ padding: '7px 16px' }} closeButton={true}>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span>Please provide the reason for requesting the deal unlock.</span>
          <Grid container spacing={"10px"} className="align-items-start autocomplete mt-3">
            <Grid size={{ xs: 6 }}>
              <VirtualizedAutocomplete
                isLoading={isPeriodLockTranchesLoading}
                isMultiple={true}
                isObject={true}
                isWritable={true}
                filterOn="Select Tranches"
                options={isPeriodLockTranchesLoading ? [] : tranches}
                selectedOptions={selectedTranches || []}
                handleSelectChange={(filterOn, newValue) => setSelectedTranches(newValue)}
              />
            </Grid>
            <Grid size={{ xs: 12 }} className="form-text-field">
              <TextField
                label="Deal Unlock Reason"
                id="deal_unlock_reason"
                multiline
                rows={3}
                value={dealUnlockReason || ""}
                onChange={(event) => setDealUnlockReason(event.target.value)}
                slotProps={{
                  input: { readOnly: !isWritable }
                }}
              />
            </Grid>
          </Grid>
        </Modal.Body>
        <Modal.Footer className="gap-2">
          <button
            type="button"
            className="btn custom-btn btn-danger"
            onClick={handleCloseModal}
          >
            Cancel
          </button>
          <Tooltip
            title={
              selectedTranches?.length === 0
                ? "Please select at least one tranche from the dropdown above"
                : ""
            }
            arrow
            disableHoverListener={selectedTranches?.length > 0}
          >
            <span>
              <button
                type="button"
                className="btn btn-primary custom-btn"
                onClick={handleUnlockDeal}
                disabled={isLoading || selectedTranches?.length === 0}
              >
                Unlock
              </button>
            </span>
          </Tooltip>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddDealModal} onHide={() => setShowAddDealModal(false)} centered>
        <Modal.Header style={{ padding: '7px 16px' }} closeButton>
          <Modal.Title>Create New Deal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Grid container spacing={"10px"} alignItems="center" justifyContent={"space-between"}>
            {/* Entity Dropdown */}
            <Grid size={{ xs: 5.2 }}>
              <VirtualizedAutocomplete
                isLoading={isEntitiesLoading}
                isMultiple={false}
                isObject={true}
                isDisabled={!isWritable || createLinkDeal}
                isWritable={isWritable && !createLinkDeal}
                filterOn="Select Entity"
                options={isEntitiesLoading ? [] : entitiesForCopy}
                selectedOptions={selectedEntity || null}
                handleSelectChange={(filterOn, newValue) => {
                  setSelectedEntity(newValue);
                  if (newValue) {
                    setCreateLinkDeal(false); // disable checkbox if entity selected
                  }
                }}
              />
            </Grid>
            <span
              style={{
                margin: "0 8px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#6c757d",
                letterSpacing: "0.5px",
              }}
            >
              OR
            </span>

            {/* Create Link Deal Checkbox */}
            <Grid size={{ xs: 5.5 }} className="d-flex align-items-center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createLinkDeal}
                    disabled={!!selectedEntity}
                    onChange={(e) => {
                      setCreateLinkDeal(e.target.checked);
                      if (e.target.checked) {
                        setSelectedEntity(null);
                      }
                    }}
                  />
                }
                label={
                  <Tooltip
                    title="This option creates linked deals for FZE and Vanitra. Any changes made in one deal will automatically apply to the other."
                    placement="right"
                    arrow
                  >
                    <span className="d-flex align-items-center gap-1">
                      Create linked deals
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: "#6c757d",
                          cursor: "pointer",
                        }}
                      />
                    </span>
                  </Tooltip>
                }
              />
            </Grid>
          </Grid>
        </Modal.Body>

        <Modal.Footer className="gap-2">
          <button
            type="button"
            className="btn custom-btn btn-danger"
            onClick={() => setShowAddDealModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary custom-btn"
            onClick={() => handleAddDeal(false, null, null)}
            disabled={
              isLoading ||
              (!selectedEntity && !createLinkDeal)
            }
          >
            {isLoading ? "Creating..." : "Create Deal"}
          </button>

        </Modal.Footer>
      </Modal>

      {/* Link Deal Modal */}
      <Modal show={showLinkDealModal} onHide={() => setShowLinkDealModal(false)} centered>
        <Modal.Header style={{ padding: '7px 16px' }} closeButton>
          <Modal.Title>Link Deal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Grid container spacing={"10px"}>
            <Grid size={{ xs: 6 }}>
              <VirtualizedAutocomplete
                isLoading={isDealsListForCopyLoading}
                isMultiple={false}
                isObject={true}
                isWritable={isWritable}
                filterOn="Select Deal to Link"
                options={isDealsListForCopyLoading ? [] : dealListForAutoPopulate}
                selectedOptions={selectedDealForAutopopulate || null}
                handleSelectChange={(filterOn, newValue) => setSelectedDealForAutopopulate(newValue)}
              />
            </Grid>
          </Grid>
        </Modal.Body>
        <Modal.Footer className="gap-2">

          <button
            type="button"
            className="btn custom-btn btn-danger"
            onClick={() => setShowLinkDealModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary custom-btn"
            onClick={handleLinkDeal}
            disabled={isLoading || !selectedDealForAutopopulate}
          >
            {isLoading ? "Linking..." : "Link Deal"}
          </button>

        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Deals;